/**
 * Seed questionnaire definitions — a verbatim snapshot of the FIVE JSON files the
 * live service loads today:
 *
 *   SalesRecommendationHub/src/main/resources/questionnaires/q_1_1..q_1_5.json
 *   (generated from origin/mainline, 2026-10-28)
 *
 * They are checked in rather than fetched so the tree surface renders the real
 * shape before the service grows a definition API. The service holds these in a
 * `static {}` block (QuestionnaireManager), so today "edit a question" means
 * "edit this JSON and redeploy the Lambda" — the whole point of this surface is
 * to replace that, and the first step is being able to SEE what is in there.
 *
 * DO NOT hand-edit the five `NODES_Q_1_*` arrays. Regenerate them from the backend
 * package so the snapshot cannot drift into something the engine never saw.
 * `NODES_FRONTEND_ENTRY` is different: Q0.1 and Q0.2 have no backend source to
 * regenerate from — they are transcribed from AgentHubPortal's `staticQuestions.tsx`,
 * so correcting that transcription is editing the right thing. Once GetAllQuestionCandidates (or a
 * definition API) is wired up, this file becomes the offline fallback only.
 *
 * Shape note: the files are bare ARRAYS of nodes, not `{firstNodeId, nodes}`. The
 * entry node is the one with `isFirstNode: true`; QuestionnaireGenerator.enhance()
 * falls back to "first in array order", which is why `entryNodeOf` in projection.ts
 * keeps that same fallback instead of assuming the flag is always set.
 */
import type { QuestionnaireDefinition, QuestionnaireNode } from '../types';

const NODES_Q_1_1: QuestionnaireNode[] = [
  {
    "nodeId": "Q1.1.1",
    "qualifiedNodeId": "Q_1_1_1",
    "questionText": {
      "id": "Is the new workload acquisition in Global Regions or China Regions?",
      "en": "Is the new workload acquisition in Global Regions or China Regions?",
      "cn": "新工作负载是在全球区域还是中国区域?"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.1.a",
        "optionText": {
          "id": "a) Global Regions",
          "en": "a) Global Regions",
          "cn": "a) 全球区域"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.1.b",
        "optionText": {
          "id": "b) China Regions",
          "en": "b) China Regions",
          "cn": "b) 中国区域"
        },
        "value": "b"
      }
    ],
    "nextQuestionRules": [
      {
        "expression": "metric['Q_1_1_1'] === 'a'",
        "nextQuestionId": "Q1.1.2_a1"
      },
      {
        "expression": "metric['Q_1_1_1'] === 'b'",
        "nextQuestionId": "Q1.1.2_b1"
      }
    ],
    "note": {
      "id": "notes: if the new workload is in both Global and China Regions, please use Credit Program Advisor to input Global Regions and China Regions information & investment asks separately",
      "en": "notes: if the new workload is in both Global and China Regions, please use Credit Program Advisor to input Global Regions and China Regions information & investment asks separately",
      "cn": "注意：如果新工作负载同时在全球和中国区域，请分别使用 Credit Program Advisor 输入全球区域和中国区域信息及申请公司资源支持的请求"
    },
    "programRules": [
      {
        "expression": "metric['Q_1_1_1'] === 'b'",
        "values": [
          "China Regions acquire new"
        ]
      }
    ],
    "isFirstNode": true,
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.2_a1",
    "qualifiedNodeId": "Q_1_1_2_a1",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of new workload?",
      "en": "What is the Gross Spend ARR ($K) of new workload?",
      "cn": "新工作负载的 Gross Spend ARR（$K）是多少?"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.1.2_a2",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.2_a2",
    "qualifiedNodeId": "Q_1_1_2_a2",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of existing workloads in this account?",
      "en": "What is the Gross Spend ARR ($K) of existing workloads in this account?",
      "cn": "此客户现有工作负载的 Gross Spend ARR（$K）是多少？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.1.3",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "programRules": [
      {
        "expression": "metric['Q_1_1_9'] * (metric['Q_1_1_2_a2'] + metric['Q_1_1_2_a1'])  >= 5000",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": " (metric['Q_1_1_2_a2'] + metric['Q_1_1_2_a1']) >= 300",
        "values": [
          "Seller-led Cross Service PPA",
          "SCE-led Cross Service PPA",
          "SCE-led Service Specific PPA",
          "Seller-led Service Specific PPA"
        ]
      }
    ],
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.2_b1",
    "qualifiedNodeId": "Q_1_1_2_b1",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of new workload?",
      "en": "What is the Gross Spend ARR ($K) of new workload?",
      "cn": "新工作负载的 Gross Spend ARR ($K) 是多少?"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.1.2_b2",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.2_b2",
    "qualifiedNodeId": "Q_1_1_2_b2",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of existing workloads in this account?",
      "en": "What is the Gross Spend ARR ($K) of existing workloads in this account?",
      "cn": "此客户现有工作负载的 Gross Spend ARR（$K）是多少？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.1.3",
    "dataType": "NUMBER",
    "isLastNode": false,
    "programRules": [
      {
        "expression": "metric['Q_1_1_9'] * (metric['Q_1_1_2_b2'] + metric['Q_1_1_2_b1'])  >= 5000",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": "(metric['Q_1_1_2_b2'] + metric['Q_1_1_2_b1']) >= 300",
        "values": [
          "Seller-led Cross Service PPA",
          "SCE-led Cross Service PPA",
          "SCE-led Service Specific PPA",
          "Seller-led Service Specific PPA"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    }
  },
  {
    "nodeId": "Q1.1.3",
    "qualifiedNodeId": "Q_1_1_3",
    "questionText": {
      "id": "Does this account have an existing PPA?",
      "en": "Does this account have an existing PPA?",
      "cn": "此客户是否有现有PPA？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.3.a",
        "optionText": {
          "id": "a) yes, SCE-led cross-service PPA",
          "en": "a) yes, SCE-led cross-service PPA",
          "cn": "a) 是，SCE主导的跨服务PPA"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.3.b",
        "optionText": {
          "id": "b) yes, seller-led cross-service PPA",
          "en": "b) yes, seller-led cross-service PPA",
          "cn": "b) 是，销售主导的跨服务PPA"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.1.3.c",
        "optionText": {
          "id": "c) yes, SCE-led service specific PPA",
          "en": "c) yes, SCE-led service specific PPA",
          "cn": "c) 是， SCE-led service 特定服务PPA"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.1.3.d",
        "optionText": {
          "id": "d) yes, seller-led service specific PPA (PARC)",
          "en": "d) yes, seller-led service specific PPA (PARC)",
          "cn": "d) 是， SCE-led service 特定服务PPA (PARC)"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.1.3.e",
        "optionText": {
          "id": "e) no PPA",
          "en": "e) no PPA",
          "cn": "e) 无PPA"
        },
        "value": "e"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: 1) this is a multiple choice question: c or d can be stacked with a or b;\n2) if there is a partner led PPA with SCE involvement, choose \"SCE-led cross-service PPA\". If there is a partner led core deal PPA, choose \"seller-led  cross-service PPA\".\n",
      "cn": "注意：1) 这是一个多选题：c或d可以与a或b叠加；\n2) 如果有SCE参与的 Partner-led PPA，选择\"SCE-led cross-service PPA\"。如果Partner-led core deal PPA，选择\"seller-led  cross-service PPA\""
    },
    "nextNodeId": "Q1.1.3.1",
    "multiSelect": true,
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.3.1",
    "qualifiedNodeId": "Q_1_1_3_1",
    "questionText": {
      "id": "Does this account intend to sign a new PPA, renew existing PPA, terminate existing PPA, or terminate existing PPA and sign a new PPA with new workload acquisition in 60 days?",
      "en": "Does this account intend to sign a new PPA, renew existing PPA, terminate existing PPA, or terminate existing PPA and sign a new PPA with new workload acquisition in 60 days?",
      "cn": "此客户是否计划在60天内签署新PPA、续签现有PPA、终止现有PPA，或终止现有PPA并签署新的PPA"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.3.1.a",
        "optionText": {
          "id": "a) yes, SCE-led cross-service PPA",
          "en": "a) yes, SCE-led cross-service PPA",
          "cn": "a) 是，SCE主导的跨服务PPA"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.3.1.b",
        "optionText": {
          "id": "b) yes, seller-led cross-service PPA",
          "en": "b) yes, seller-led cross-service PPA",
          "cn": "b) 是，销售主导的跨服务PPA"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.1.3.1.c",
        "optionText": {
          "id": "c) yes, SCE-led service specific PPA",
          "en": "c) yes, SCE-led service specific PPA",
          "cn": "c) 是，SCE-led 特定服务PPA"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.1.3.1.d",
        "optionText": {
          "id": "d) yes, seller-led service specific PPA (PARC)",
          "en": "d) yes, seller-led service specific PPA (PARC)",
          "cn": "d) 是，特定服务PPA (PARC)"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.1.3.1.e",
        "optionText": {
          "id": "e) R-PPA",
          "en": "e) R-PPA",
          "cn": "e) R-PPA"
        },
        "value": "f"
      },
      {
        "optionId": "Answer_1.1.3.1.f",
        "optionText": {
          "id": "f) no PPA",
          "en": "f) no PPA",
          "cn": "f) 无PPA"
        },
        "value": "e"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: 1) choose the answer that matches the end state of PPA in this account in 60 days;\n2) this is a multiple choice question: c or d can be stacked with a or b;\n3) if there is a partner led PPA with SCE involvement, choose \"SCE-led cross-service PPA\". If there is a partner led core deal PPA, choose \"seller-led  cross-service PPA\".\n",
      "cn": "注意：1) 请选择未来60天内此客户PPA的最终状态；\n2) 这是一个多选题：c或d可以与a或b叠加；\n3) 如果有SCE参与的 Partner-led PPA，选择\"SCE-led cross-service PPA\"。如果Partner-led core deal PPA，选择\"seller-led  cross-service PPA\""
    },
    "programRules": [
      {
        "expression": "metric['Q_1_1_3_1'].includes('a') ",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_1_3_1'].includes('b') ",
        "values": [
          "Seller-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_1_3_1'].includes('c')",
        "values": [
          "SCE-led Service Specific PPA"
        ]
      },
      {
        "expression": "metric['Q_1_1_3_1'].includes('d')",
        "values": [
          "Seller-led Service Specific PPA"
        ]
      },
      {
        "expression": "metric['Q_1_1_3_1'].includes('f')",
        "values": [
          "R-PPA"
        ]
      },
      {
        "expression": "metric['Q_1_1_1'] === 'a' && hasAnyMatch('Q_1_1_3_1', ['a','b','c','d','e','f'])  ",
        "values": [
          "VIP Funding",
          "Compete Funding"
        ]
      },
      {
        "expression": "metric['Q_1_1_1'] === 'b' && hasAnyMatch('Q_1_1_3_1', ['a','b','c','d','e','f']) ",
        "values": [
          "VIP Funding",
          "Compete Funding"
        ]
      }
    ],
    "multiSelect": true,
    "nextNodeId": "Q1.1.4",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.4",
    "qualifiedNodeId": "Q_1_1_4",
    "questionText": {
      "id": "Is this a migration?",
      "en": "Is this a migration?",
      "cn": "这是迁移项目吗？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.4.a",
        "optionText": {
          "id": "a) yes",
          "en": "a) yes",
          "cn": "a) 是"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.4.b",
        "optionText": {
          "id": "b) no",
          "en": "b) no",
          "cn": "b) 否"
        },
        "value": "b"
      }
    ],
    "nextQuestionRules": [
      {
        "expression": "metric['Q_1_1_4'] === 'a'",
        "nextQuestionId": "Q1.1.4.1"
      },
      {
        "expression": "metric['Q_1_1_4'] === 'b'",
        "nextQuestionId": "Q1.1.5"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_1_1'] === 'a' &&  metric['Q_1_1_4'] === 'a' && metric['Q_1_1_2_a1'] >= 500",
        "values": [
          "MAP 2.0"
        ]
      },
      {
        "expression": "metric['Q_1_1_1'] === 'a' &&  metric['Q_1_1_4'] === 'a' && metric['Q_1_1_2_a1'] < 500",
        "values": [
          "MAP Lite"
        ]
      },
      {
        "expression": "metric['Q_1_1_1'] === 'b' &&  metric['Q_1_1_4'] === 'a'",
        "values": [
          "GCR MAP"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "definition: Migration is to move customer workload from IDC or other CSPs to AWS",
      "cn": "定义：迁移是指将客户工作负载从IDC或其他云服务提供商迁移到AWS"
    },
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.4.1",
    "qualifiedNodeId": "Q_1_1_4_1",
    "questionText": {
      "id": "Is the migration from IDC or does the migration involve SAP, VMware, or Mainframe workloads?",
      "en": "Is the migration from IDC or does the migration involve SAP, VMware, or Mainframe workloads?",
      "cn": "工作迁移是否从IDC迁移或涉及SAP、Oracle、Vmware, Mainframe, 或DB & analytics工作负载？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.4.1.a",
        "optionText": {
          "id": "a) no",
          "en": "a) no",
          "cn": "a) 否"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.4.1.b",
        "optionText": {
          "id": "b) SAP or Oracle",
          "en": "b) SAP or Oracle",
          "cn": "b) SAP or Oracle"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.1.4.1.c",
        "optionText": {
          "id": "c) VMware",
          "en": "c) VMware",
          "cn": "c) VMware"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.1.4.1.d",
        "optionText": {
          "id": "d) Mainframe",
          "en": "d) Mainframe",
          "cn": "d) 大型机"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.1.4.1.e",
        "optionText": {
          "id": "e) IDC",
          "en": "e) IDC",
          "cn": "e) IDC"
        },
        "value": "e"
      },
      {
        "optionId": "Answer_1.1.4.1.f",
        "optionText": {
          "id": "f) DB & analytics",
          "en": "f) DB & analytics",
          "cn": "f) DB & analytics"
        },
        "value": "f"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_1_4_1'] === 'b'",
        "values": [
          "MAP for SAP & Oracle"
        ]
      },
      {
        "expression": "metric['Q_1_1_4_1'] === 'c'",
        "values": [
          "VMware MAP"
        ]
      },
      {
        "expression": "metric['Q_1_1_4_1'] === 'd'",
        "values": [
          "Mainframe Must Win"
        ]
      },
      {
        "expression": "metric['Q_1_1_4_1'] === 'e' && (metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1'] + metric['Q_1_1_2_a2']||metric['Q_1_1_2_b1']) >= 1000",
        "values": [
          "ACDC"
        ]
      },
      {
        "expression": "metric['Q_1_1_4_1'] === 'f'",
        "values": [
          "DB & Analytics MAP"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "nextNodeId": "Q1.1.5",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.5",
    "qualifiedNodeId": "Q_1_1_5",
    "questionText": {
      "id": "Is partner involved in this new workload acquisition?",
      "en": "Is partner involved in this new workload acquisition?",
      "cn": "合作伙伴是否参与了新工作负载获取？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.5.a",
        "optionText": {
          "id": "a) yes",
          "en": "a) yes",
          "cn": "a) 是"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.5.b",
        "optionText": {
          "id": "b) no",
          "en": "b) no",
          "cn": "b) 否"
        },
        "value": "b"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: some partners may voluntarily subsidize end user with SPP refund or SP Benefit from partner led PPA. If the partner involved in new workload acquistition will subsdize end user, choose \"yes\". If the partner involved in new workload acquistition will NOT subsdize end user, choose \"no\".",
      "cn": "注意：某些合作伙伴会选择用SPP refund或SP Benefit from partner led PPA来补贴最终用户。如果参与新工作负载获取的合作伙伴将补贴最终用户，选择\"yes\"。如果合作伙伴不补贴最终用户，选择\"no\""
    },
    "programRules": [
      {
        "expression": "metric['Q_1_1_5'] === 'a'",
        "values": [
          "SPP Refund or SP Benefit"
        ]
      }
    ],
    "autoAnswerRules": [
      {
        "expression": "metric['Q_1_1_3_1'].includes('f')",
        "answer": "a",
        "multipleAnswers": [],
        "skipUserInput": true,
        "reason": {
          "id": "",
          "en": "",
          "cn": ""
        }
      }
    ],
    "nextNodeId": "Q1.1.6",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.6",
    "qualifiedNodeId": "Q_1_1_6",
    "questionText": {
      "id": "Is this an ISV account expansion into a new Geo Region?",
      "en": "Is this an ISV account expansion into a new Geo Region?",
      "cn": "这是否为ISV客户扩展到一个新的国际地理区域？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.6.a",
        "optionText": {
          "id": "a) yes",
          "en": "a) yes",
          "cn": "a) 是"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.6.b",
        "optionText": {
          "id": "b) no",
          "en": "b) no",
          "cn": "b) 否"
        },
        "value": "b"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: this includes both Global ISV landing in China and GCR ISV global business expansion",
      "cn": "注意：这既包括Global ISV在中国落地也包括中国的ISV出海业务扩张"
    },
    "programRules": [
      {
        "expression": "metric['Q_1_1_6'] === 'a' && ((metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1']) + (metric['Q_1_1_2_a2']||metric['Q_1_1_2_b2'])) >= 1000",
        "values": [
          "AWS Global Passport (ISV)"
        ]
      }
    ],
    "nextNodeId": "Q1.1.7",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.7",
    "qualifiedNodeId": "Q_1_1_7",
    "questionText": {
      "id": "Is this a T2K acquire account?",
      "en": "Is this a T2K acquire account?",
      "cn": "这是否是T2K acquire 客户？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.7.a",
        "optionText": {
          "id": "a) yes",
          "en": "a) yes",
          "cn": "a) 是"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.7.b",
        "optionText": {
          "id": "b) no",
          "en": "b) no",
          "cn": "b) 否"
        },
        "value": "b"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_1_7'] === 'a' && !hasKey('SCE-led cross service PPA')",
        "values": [
          "T2K Winroom"
        ]
      },
      {
        "expression": "metric['Q_1_1_7'] === 'a' && hasKey('SCE-led cross service PPA')",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "nextNodeId": "Q1.1.8",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.8",
    "qualifiedNodeId": "Q_1_1_8",
    "questionText": {
      "id": "Is this a GFD account or Startup account?",
      "en": "Is this a GFD account or Startup account?",
      "cn": "这是否是GFD客户还是Startup客户？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.1.8.a",
        "optionText": {
          "id": "a) GFC account",
          "en": "a) GFC account",
          "cn": "a) GFC账户"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.1.8.b",
        "optionText": {
          "id": "b) Startup account",
          "en": "b) Startup account",
          "cn": "b) 初创账户"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.1.8.c",
        "optionText": {
          "id": "c) no",
          "en": "c) no",
          "cn": "c) 否"
        },
        "value": "c"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_1_8'] === 'a'",
        "values": [
          "Greenfield FastStart"
        ]
      },
      {
        "expression": "metric['Q_1_1_8'] === 'b' &&  ((metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1']) + (metric['Q_1_1_2_a2']||metric['Q_1_1_2_b2']))  < 1",
        "values": [
          "GCR Activate"
        ]
      },
      {
        "expression": "metric['Q_1_1_8'] === 'b' && ((metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1']) + (metric['Q_1_1_2_a2']||metric['Q_1_1_2_b2'])) <= 120",
        "values": [
          "GCR Hot Startup"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "nextNodeId": "Q1.1.9",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.9",
    "qualifiedNodeId": "Q_1_1_9",
    "questionText": {
      "id": "How many years of Deal Resources investment support do you need?",
      "en": "How many years of Deal Resources investment support do you need?",
      "cn": "您需要公司几年的资源投资支持？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.5.a",
        "optionText": {
          "id": "1 year",
          "en": "1 year",
          "cn": "1 年"
        },
        "value": "1"
      },
      {
        "optionId": "Answer_1.2.5.b",
        "optionText": {
          "id": "2 year",
          "en": "2 year",
          "cn": "2 年"
        },
        "value": "2"
      },
      {
        "optionId": "Answer_1.2.5.c",
        "optionText": {
          "id": "3 year",
          "en": "3 year",
          "cn": "3 年"
        },
        "value": "3"
      },
      {
        "optionId": "Answer_1.2.5.d",
        "optionText": {
          "id": "4 year",
          "en": "4 year",
          "cn": "4 年"
        },
        "value": "4"
      },
      {
        "optionId": "Answer_1.2.5.e",
        "optionText": {
          "id": "5 year",
          "en": "5 year",
          "cn": "5 年"
        },
        "value": "5"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: fill in the number of years that you are formulating your deal structure to win against competition",
      "cn": "注意：填写您为赢得新工作负载制定的交易结构中计划的年数"
    },
    "dataType": "NUMBER",
    "nextNodeId": "Q1.1.10",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.1.10",
    "qualifiedNodeId": "Q_1_1_10",
    "questionText": {
      "id": "Please indicate the investment ratio of new workload Gross Spend that you are proposing to win",
      "en": "Please indicate the investment ratio of new workload Gross Spend that you are proposing to win",
      "cn": "为赢得新工作负载，需要公司投资支持金额占Gross Spend的比例"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "notes: investment ratio of new workload gross spend = 1-(winning price ($K) / new workload Gross Spend ($K))",
      "cn": "注意：需要公司投资支持金额占Gross Spend的比例 = 1 -（赢单价格 ($K) / 新工作负载的Gross Spend ($K)）"
    },
    "isLastNode": true
  }
];

const NODES_Q_1_2: QuestionnaireNode[] = [
  {
    "nodeId": "Q1.2.1",
    "qualifiedNodeId": "Q_1_2_1",
    "questionText": {
      "id": "Is the retention workload in Global Regions or China Regions?",
      "en": "Is the retention workload in Global Regions or China Regions?",
      "cn": "需保留的工作负载是在全球区域还是中国区域？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.1.a",
        "optionText": {
          "id": "a) Global Regions",
          "en": "a) Global Regions",
          "cn": "a) 全球区域"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.2.1.b",
        "optionText": {
          "id": "b) China Regions",
          "en": "b) China Regions",
          "cn": "b) 中国区域"
        },
        "value": "b"
      }
    ],
    "nextQuestionRules": [
      {
        "expression": "metric['Q_1_2_1'] === 'a'",
        "nextQuestionId": "Q1.2.2_a1"
      },
      {
        "expression": "metric['Q_1_2_1'] === 'b'",
        "nextQuestionId": "Q1.2.2_b1"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: if the retention workload is in both Global and China Regions, please use Deal Desk Tool to input Global Regions and China Regions information & investment asks separately",
      "cn": "注意：如果需保留的工作负载同时在全球和中国区域，请分别使用Credit Program Advisor 输入全球区域和中国区域信息及申请公司资源支持的请求 "
    },
    "isFirstNode": true,
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.2_a1",
    "qualifiedNodeId": "Q_1_2_2_a1",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of the retention workload?",
      "en": "What is the Gross Spend ARR ($K) of the retention workload?",
      "cn": "需保留的工作负载的 Gross Spend ARR ($K) 是多少？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "nextNodeId": "Q1.2.2_a2",
    "dataType": "NUMBER",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.2_a2",
    "qualifiedNodeId": "Q_1_2_2_a2",
    "questionText": {
      "id": "What is the current Gross Spend ARR ($K) of this account (including the retention workload)?",
      "en": "What is the current Gross Spend ARR ($K) of this account (including the retention workload)?",
      "cn": "此客户现有工作负载的Gross Spend ARR ($K) 是多少（包括需保留的工作负载）？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.2.3",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "programRules": [
      {
        "expression": "(metric['Q_1_2_2_a2'] * metric['Q_1_2_5']) >= 5000 ",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_2_a2'] > 300",
        "values": [
          "SCE-led Cross Service PPA",
          "Seller-led Cross Service PPA",
          "SCE-led Service Specific PPA",
          "Seller-led Service Specific PPA"
        ]
      }
    ],
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.2_b1",
    "qualifiedNodeId": "Q_1_2_2_b1",
    "questionText": {
      "id": "What is the Gross Spend ARR ($K) of the retention workload?",
      "en": "What is the Gross Spend ARR ($K) of the retention workload?",
      "cn": "需保留的工作负载的 Gross Spend ARR ($K) 是多少？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.2.2_b2",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.2_b2",
    "qualifiedNodeId": "Q_1_2_2_b2",
    "questionText": {
      "id": "What is the current Gross Spend ARR ($K) of this account (including the retention workload)?",
      "en": "What is the current Gross Spend ARR ($K) of this account (including the retention workload)?",
      "cn": "此客户现有工作负载的Gross Spend ARR ($K) 是多少（包括需保留的工作负载）？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "nextNodeId": "Q1.2.3",
    "dataType": "NUMBER",
    "note": {
      "id": "",
      "en": "definition: Gross Spend is usage value ($K) after (deducting) technical optimization (e.g. graviton adoption) and public pricing cost optimization (e.g. RI, SP, tiered pricing, volume discount), but before (NOT deducting) Private pricing discount (PPA discount), SPP refund and AWS investment program resources, e.g. MAP credit, VIP funding, Compete funding",
      "cn": "定义：Gross Spend 是指在扣除技术优化（如graviton采用）和 public pricing 方案成本优化（如RI、SP、阶梯定价、总量折扣）后的usage。Private Pricing discount（PPA discount）、SPP refund和AWS销售投资项目的支持（如MAP credit、VIP funding、Compete funding）等不用从Gross Spend扣除"
    },
    "programRules": [
      {
        "expression": "(metric['Q_1_2_2_b2'] * metric['Q_1_2_5']) >= 5000 ",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_2_b2'] > 300",
        "values": [
          "SCE-led Cross Service PPA",
          "Seller-led Cross Service PPA",
          "SCE-led Service Specific PPA",
          "Seller-led Service Specific PPA"
        ]
      }
    ],
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.3",
    "qualifiedNodeId": "Q_1_2_3",
    "questionText": {
      "id": "Does this account have an existing PPA?",
      "en": "Does this account have an existing PPA?",
      "cn": "此客户是否有现有PPA？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.3.a",
        "optionText": {
          "id": "a) yes, SCE-led cross-service PPA",
          "en": "a) yes, SCE-led cross-service PPA",
          "cn": "是，SCE主导的跨服务PPA"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.2.3.b",
        "optionText": {
          "id": "b) yes, seller-led cross-service PPA",
          "en": "b) yes, seller-led cross-service PPA",
          "cn": "是，销售主导的跨服务PPA"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.2.3.c",
        "optionText": {
          "id": "c) yes, SCE-led service specific PPA",
          "en": "c) yes, SCE-led service specific PPA",
          "cn": "c) yes, SCE-led service specific PPA"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.2.3.d",
        "optionText": {
          "id": "d) yes, seller-led service specific PPA (PARC)",
          "en": "d) yes, seller-led service specific PPA (PARC)",
          "cn": "d) yes, seller-led service specific PPA (PARC)"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.2.3.e",
        "optionText": {
          "id": "e) no PPA",
          "en": "e) no PPA",
          "cn": "e) 无PPA"
        },
        "value": "e"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: 1) this is a multiple choice question: c or d can be stacked with a or b;\n2) if there is a partner led PPA with SCE involvement, choose \"SCE-led cross-service PPA\". If there is a partner led core deal PPA, choose \"seller-led  cross-service PPA\".\n",
      "cn": "注意：1) 这是一个多选题：c或d可以与a或b叠加；\n2) 如果有SCE参与的 Partner-led PPA，选择\"SCE-led cross-service PPA\"。如果Partner-led core deal PPA，选择\"seller-led  cross-service PPA\""
    },
    "nextNodeId": "Q1.2.3.1",
    "multiSelect": true,
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.3.1",
    "qualifiedNodeId": "Q_1_2_3_1",
    "questionText": {
      "id": "Does this account intend to sign a new PPA, renew existing PPA, terminate existing PPA, or terminate existing PPA and sign a new PPA with new workload acquisition in 60 days?",
      "en": "Does this account intend to sign a new PPA, renew existing PPA, terminate existing PPA, or terminate existing PPA and sign a new PPA with new workload acquisition in 60 days?",
      "cn": "此客户是否计划在60天内签署新PPA、续签现有PPA、终止现有PPA，或终止现有PPA并签署新的PPA？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.3.a",
        "optionText": {
          "id": "a) yes, SCE-led cross-service PPA",
          "en": "a) yes, SCE-led cross-service PPA",
          "cn": "是，SCE主导的跨服务PPA"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.2.3.b",
        "optionText": {
          "id": "b) yes, seller-led cross-service PPA",
          "en": "b) yes, seller-led cross-service PPA",
          "cn": "b) 是，销售主导的跨服务PPA"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.2.3.c",
        "optionText": {
          "id": "c) yes, SCE-led service specific PPA",
          "en": "c) yes, SCE-led service specific PPA",
          "cn": "c) yes, SCE-led service specific PPA"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.2.3.d",
        "optionText": {
          "id": "d) yes, seller-led service specific PPA (PARC)",
          "en": "d) yes, seller-led service specific PPA (PARC)",
          "cn": "d) yes, seller-led service specific PPA (PARC)"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.2.3.e",
        "optionText": {
          "id": "e) R-PPA",
          "en": "e) R-PPA",
          "cn": "e) R-PPA"
        },
        "value": "f"
      },
      {
        "optionId": "Answer_1.2.3.f",
        "optionText": {
          "id": "f) no PPA",
          "en": "f) no PPA",
          "cn": "f) 无PPA"
        },
        "value": "e"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_2_3_1'].includes('a') ",
        "values": [
          "SCE-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_3_1'].includes('b') ",
        "values": [
          "Seller-led Cross Service PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_3_1'].includes('c')",
        "values": [
          "SCE-led Service Specific PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_3_1'].includes('d')",
        "values": [
          "Seller-led Service Specific PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_3_1'].includes('f')",
        "values": [
          "R-PPA"
        ]
      },
      {
        "expression": "metric['Q_1_2_1'] ==='a' && hasAnyMatch('Q_1_2_3_1', ['a', 'b','c','d','e','f']) ",
        "values": [
          "VIP Funding",
          "Compete Funding"
        ]
      },
      {
        "expression": "metric['Q_1_2_1'] ==='b'  && hasAnyMatch('Q_1_2_3_1', ['a','b','c','d','e','f']) ",
        "values": [
          "VIP Funding",
          "Compete Funding"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "notes: 1) choose the answer that matches the end state of PPA in this account in 60 days;\n2) this is a multiple choice question: c or d can be stacked with a or b;\n3) if there is a partner led PPA with SCE involvement, choose \"SCE-led cross-service PPA\". If there is a partner led core deal PPA, choose \"seller-led  cross-service PPA\".\n",
      "cn": "注意：1) 请选择未来60天内此客户PPA的最终状态；\n2) 这是一个多选题：c或d可以与a或b叠加；\n3) 如果有SCE参与的 Partner-led PPA，选择\"SCE-led cross-service PPA\"。如果Partner-led core deal PPA，选择\"seller-led  cross-service PPA\""
    },
    "multiSelect": true,
    "nextNodeId": "Q1.2.4",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.4",
    "qualifiedNodeId": "Q_1_2_4",
    "questionText": {
      "id": "Is partner involved in this workload retention?",
      "en": "Is partner involved in this workload retention?",
      "cn": "合作伙伴是否参与了现有工作负载留存？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.4.a",
        "optionText": {
          "id": "a) yes",
          "en": "a) yes",
          "cn": "a) 是"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.2.4.b",
        "optionText": {
          "id": "b) no",
          "en": "b) no",
          "cn": "b) 否"
        },
        "value": "b"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_2_4'] === 'a'",
        "values": [
          "SPP Refund or SP Benefit"
        ]
      }
    ],
    "nextNodeId": "Q1.2.5",
    "note": {
      "id": "",
      "en": "notes: some partners may voluntarily subsidize end user with SPP refund or SP Benefit from partner led PPA. If the partner involved in new workload acquistition will subsdize end user, choose \"yes\". If the partner involved in new workload acquistition will NOT subsdize end user, choose \"no\".",
      "cn": "注意：某些合作伙伴会选择用SPP refund或SP Benefit from partner led PPA来补贴最终用户。如果参与现有工作负载留存的合作伙伴将补贴最终用户，选择\"yes\"。如果合作伙伴不补贴最终用户，选择\"no\""
    },
    "autoAnswerRules": [
      {
        "expression": "metric['Q_1_2_3_1'].includes('f')",
        "answer": "a",
        "multipleAnswers": [],
        "skipUserInput": true,
        "reason": {
          "id": "",
          "en": "",
          "cn": ""
        }
      }
    ],
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.5",
    "qualifiedNodeId": "Q_1_2_5",
    "questionText": {
      "id": "How many years of Deal Resources investment support do you need?",
      "en": "How many years of Deal Resources investment support do you need?",
      "cn": "您需要公司几年的资源投资支持？"
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.2.5.a",
        "optionText": {
          "id": "1 year",
          "en": "1 year",
          "cn": "1 年"
        },
        "value": "1"
      },
      {
        "optionId": "Answer_1.2.5.b",
        "optionText": {
          "id": "2 year",
          "en": "2 year",
          "cn": "2 年"
        },
        "value": "2"
      },
      {
        "optionId": "Answer_1.2.5.c",
        "optionText": {
          "id": "3 year",
          "en": "3 year",
          "cn": "3 年"
        },
        "value": "3"
      },
      {
        "optionId": "Answer_1.2.5.d",
        "optionText": {
          "id": "4 year",
          "en": "4 year",
          "cn": "4 年"
        },
        "value": "4"
      },
      {
        "optionId": "Answer_1.2.5.e",
        "optionText": {
          "id": "5 year",
          "en": "5 year",
          "cn": "5 年"
        },
        "value": "5"
      }
    ],
    "note": {
      "id": "",
      "en": "notes: fill in the number of years that you are formulating your deal structure to win against competition",
      "cn": "注意：填写您为留存现有工作负载制定的交易结构中计划的年数"
    },
    "nextNodeId": "Q1.2.6",
    "dataType": "NUMBER",
    "isLastNode": false
  },
  {
    "nodeId": "Q1.2.6",
    "qualifiedNodeId": "Q_1_2_6",
    "questionText": {
      "id": "Please indicate the investment ratio of Retention Workload Gross Spend that you are proposing to win",
      "en": "Please indicate the investment ratio of Retention Workload Gross Spend that you are proposing to win",
      "cn": "为保留现有工作负载，需要公司投资支持金额占Gross Spend的比例？"
    },
    "questionType": "INPUT_NUMBER",
    "answerOptions": [],
    "note": {
      "id": "",
      "en": "notes: investment ratio of retention workload gross spend = 1-(winning price ($K) / retention workload Gross Spend ($K))",
      "cn": "注意：需要公司投资支持金额占Gross Spend的比例 = 1 -（赢单价格 ($K) / 需保留的工作负载的Gross Spend ($K)）"
    },
    "dataType": "NUMBER",
    "isLastNode": true
  }
];

const NODES_Q_1_3: QuestionnaireNode[] = [
  {
    "nodeId": "Q1.3.1",
    "qualifiedNodeId": "Q_1_3_1",
    "questionText": {
      "id": "What is the purpose of the PoC?",
      "en": "What is the purpose of the PoC?",
      "cn": ""
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.3.1.a",
        "optionText": {
          "id": "a) IVT Solution PoC",
          "en": "a) IVT Solution PoC",
          "cn": "a) IVT Solution PoC"
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.3.1.b",
        "optionText": {
          "id": "b) GenAI Service PoC",
          "en": "b) GenAI Service PoC",
          "cn": "b) GenAI Service PoC"
        },
        "value": "b"
      },
      {
        "optionId": "Answer_1.3.1.c",
        "optionText": {
          "id": "c) Non GenAI Service PoC",
          "en": "c) Non GenAI Service PoC",
          "cn": "c) Non GenAI Service PoC"
        },
        "value": "c"
      },
      {
        "optionId": "Answer_1.3.1.d",
        "optionText": {
          "id": "d) Partner Solution PoC",
          "en": "d) Partner Solution PoC",
          "cn": "d) Partner Solution PoC"
        },
        "value": "d"
      },
      {
        "optionId": "Answer_1.3.1.e",
        "optionText": {
          "id": "e) General Platform Adoption PoC (Sales PoC)",
          "en": "e) General Platform Adoption PoC (Sales PoC)",
          "cn": "e) General Platform Adoption PoC (Sales PoC)"
        },
        "value": "e"
      }
    ],
    "programRules": [
      {
        "expression": "metric['Q_1_3_1'] === 'a'",
        "values": [
          "IVT Solution PoC"
        ]
      },
      {
        "expression": "metric['Q_1_3_1']  === 'b'",
        "values": [
          "GenAI Service PoC"
        ]
      },
      {
        "expression": "metric['Q_1_3_1'] === 'c'",
        "values": [
          "Non GenAI Service PoC"
        ]
      },
      {
        "expression": "metric['Q_1_3_1'] === 'd'",
        "values": [
          "Partner Solution PoC"
        ]
      },
      {
        "expression": "metric['Q_1_3_1'] === 'e'",
        "values": [
          "General Platform Adoption PoC (Sales PoC)"
        ]
      }
    ],
    "nextQuestionRules": [],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "isFirstNode": true,
    "isLastNode": true
  }
];

const NODES_Q_1_4: QuestionnaireNode[] = [
  {
    "nodeId": "Q1.4.1",
    "qualifiedNodeId": "Q_1_4_1",
    "questionText": {
      "id": "Customer Satisfaction or Billing Errors",
      "en": "Customer Satisfaction or Billing Errors",
      "cn": ""
    },
    "questionType": "LIST_SELECTION",
    "answerOptions": [
      {
        "optionId": "Answer_1.4.1.a",
        "optionText": {
          "id": "a) customer satisfaction",
          "en": "a) customer satisfaction",
          "cn": ""
        },
        "value": "a"
      },
      {
        "optionId": "Answer_1.4.1.b",
        "optionText": {
          "id": "b) Billing Errors",
          "en": "b) Billing Errors",
          "cn": ""
        },
        "value": "b"
      }
    ],
    "nextQuestionRules": [],
    "programRules": [
      {
        "expression": "metric['Q_1_4_1'] === 'a'",
        "values": [
          "Good Will Credit"
        ]
      },
      {
        "expression": "metric['Q_1_4_1']  === 'b'",
        "values": [
          "Good Will Credit"
        ]
      }
    ],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "isFirstNode": true,
    "isLastNode": true
  }
];

const NODES_Q_1_5: QuestionnaireNode[] = [
  {
    "nodeId": "Q1.5.1",
    "qualifiedNodeId": "Q_1_5_1",
    "questionText": {
      "id": "other purposes, please specify in next page",
      "en": "other purposes, please specify in next page",
      "cn": ""
    },
    "questionType": "INPUT_TEXT",
    "answerOptions": [],
    "nextQuestionRules": [],
    "note": {
      "id": "",
      "en": "",
      "cn": ""
    },
    "isFirstNode": true,
    "isLastNode": true
  }
];


/**
 * Which tree each answer to Q0.2 leads to. Mirrors
 * `AgentHubPortal/.../questionnaire/util.tsx::purpose_mappings`.
 */
const SUBTREE_ROUTES: { purposeAnswer: string; entryNodeId: string }[] = [
  { purposeAnswer: 'a', entryNodeId: 'Q1.1.1' },
  { purposeAnswer: 'b', entryNodeId: 'Q1.2.1' },
  { purposeAnswer: 'c', entryNodeId: 'Q1.3.1' },
  { purposeAnswer: 'd', entryNodeId: 'Q1.4.1' },
  { purposeAnswer: 'e', entryNodeId: 'Q1.5.1' },
];

/**
 * ── The two questions that live in the FRONTEND, not in any backend JSON ──────
 *
 * `AgentHubPortal/src/components/dealdesktool/questionnaire/data/staticQuestions.tsx`
 * hard-codes Q0.1 and Q0.2 and asks them BEFORE it fetches anything. Only after
 * Q0.2 is answered does it map the answer to a tree
 * (`util.tsx::purpose_mappings`: a→q_1_1 … e→q_1_5) and pull that tree from
 * `GetAllQuestionCandidates`.
 *
 * So the questionnaire a BD actually walks has never existed as one artefact:
 * two questions in a React file plus one of five JSON files on a Lambda's
 * classpath. That split is why "最多 15 题" is stated in Q0.1's note rather than
 * being derivable from any single definition.
 *
 * Transcribed here so the canvas can show the whole walk. Two deliberate
 * differences from the frontend source, both noted where they appear.
 */
const NODES_FRONTEND_ENTRY: QuestionnaireNode[] = [
  {
    nodeId: 'Q0.1',
    qualifiedNodeId: 'Q_0_1',
    questionText: {
      id: 'Please enter your 18-character SFDC Account ID',
      en: 'Please enter your 18-character SFDC Account ID',
      cn: '请输入您的18位SFDC账户ID',
    },
    /**
     * DIFFERENCE 1: the live question is `sfdcAccounts.length > 0 ? LIST_SELECTION
     * : TEXT_INPUT`, and when it is a list its options are the caller's own SFDC
     * accounts, generated per user at runtime. A definition cannot hold those, so
     * `INPUT_TEXT` is the fallback shape — and `uiComponent` is what says the control
     * is really neither: it is a named component that decides its own form.
     *
     * This is the one field that moves Q0.1 out of being hard-coded. Everything the
     * BD frontend currently decides from `nodeId === 'Q0.1'` — the searchable dropdown,
     * the account lookup, the remote id validation — follows from the component name
     * instead of from the id.
     */
    questionType: 'INPUT_TEXT',
    uiComponent: 'sfdc-account',
    answerOptions: [],
    note: {
      id: 'NOTES: Credit Program Advisor is designed to provide account team with a clear path of qualified AWS pricing & investment resources, e.g. private pricing, investment programs, when facing competition and help you win more and bigger. You will need to answer a maximum of 15 questions depending on the account and competition support complexity. Based on your inputs, the Credit Program Advisor will recommend qualified investment programs and/or private pricing combo options. ',
      en: 'NOTES: Credit Program Advisor is designed to provide account team with a clear path of qualified AWS pricing & investment resources, e.g. private pricing, investment programs, when facing competition and help you win more and bigger. You will need to answer a maximum of 15 questions depending on the account and competition support complexity. Based on your inputs, the Credit Program Advisor will recommend qualified investment programs and/or private pricing combo options. ',
      cn: '注意：Credit Program Advisor 旨在为销售团队应对竞争局面时，提供可用的AWS销售价格计划和销售投资资源的建议（包括私有定价和各类销售投资项目）。根据客户情况和竞争支持请求的复杂程度，您需要回答最多15个问题。根据您的输入，Credit Program Advisor 将提供符合客户情况和竞争支持请求的销售投资项目和/或私有定价建议组合选项供您选用',
    },
    nextNodeId: 'Q0.2',
    isFirstNode: true,
    isLastNode: false,
  },
  {
    nodeId: 'Q0.2',
    qualifiedNodeId: 'Q_0_2',
    questionText: {
      id: 'Main purpose of Deal Resources ask?',
      en: 'Main purpose of Deal Resources ask?',
      cn: '申请公司资源支持的主要目的？',
    },
    questionType: 'LIST_SELECTION',
    answerOptions: [
      { optionId: 'Answer_0.2.1', optionText: { id: 'a) 1.1 new workload acquisition', en: 'a) 1.1 new workload acquisition', cn: 'a) 1.1 新工作负载赢单' }, value: 'a' },
      { optionId: 'Answer_0.2.2', optionText: { id: 'b) 1.2 existing workload retention', en: 'b) 1.2 existing workload retention', cn: 'b) 1.2 现有工作负载留存' }, value: 'b' },
      { optionId: 'Answer_0.2.3', optionText: { id: 'c) 1.3 PoC', en: 'c) 1.3 PoC', cn: 'c) 1.3 概念验证 (PoC)' }, value: 'c' },
      { optionId: 'Answer_0.2.4', optionText: { id: 'd) 1.4 Customer Satisfaction or Billing Errors', en: 'd) 1.4 Customer Satisfaction or Billing Errors', cn: 'd) 1.4 保持客户满意度或解决账单错误' }, value: 'd' },
      { optionId: 'Answer_0.2.5', optionText: { id: 'e) 1.5 other purposes', en: 'e) 1.5 other purposes', cn: 'e) 1.5 其他目的' }, value: 'e' },
    ],
    /**
     * DIFFERENCE 2: `isLastNode: true` in the frontend source, because the React
     * flow ENDS here and hands over to whichever tree it then fetches. In the
     * merged walk it is not an end at all — it is the five-way branch, so the
     * handover is written as the `nextQuestionRules` below.
     */
    isLastNode: false,
    nextQuestionRules: SUBTREE_ROUTES.map((r) => ({
      expression: `metric['Q_0_2'] === '${r.purposeAnswer}'`,
      nextQuestionId: r.entryNodeId,
    })),
  },
];


/**
 * The whole walk as ONE graph: the two frontend questions, then all five trees
 * hanging off Q0.2.
 *
 * COMPOSED from the snapshots above rather than written out, so regenerating the
 * five `q_1_*.json` snapshots updates this automatically instead of letting a
 * second copy drift.
 *
 * The one rewrite each subtree needs: `isFirstNode` is cleared. All five files set
 * it (each was the entry of its own questionnaire), and a merged graph has exactly
 * one entry — Q0.1. `entryNodeOf` picks the flagged node, so leaving five flags
 * set would make the entry depend on array order.
 */
const NODES_ALL: QuestionnaireNode[] = [
  ...NODES_FRONTEND_ENTRY,
  ...[NODES_Q_1_1, NODES_Q_1_2, NODES_Q_1_3, NODES_Q_1_4, NODES_Q_1_5]
    .flat()
    .map((n) => (n.isFirstNode ? { ...n, isFirstNode: false } : n)),
];

/**
 * The five definitions, keyed the way the service keys them (the resource file
 * basename, which is also `Questionnaire.purpose` in DynamoDB).
 */
export const SEED_DEFINITIONS: QuestionnaireDefinition[] = [
  { id: 'q_1_1', purposeAnswer: 'a', name: { en: "New Workload Acquisition", cn: "新工作负载赢单" }, nodes: NODES_Q_1_1 },
  { id: 'q_1_2', purposeAnswer: 'b', name: { en: "Existing Workload Retention", cn: "现有工作负载留存" }, nodes: NODES_Q_1_2 },
  { id: 'q_1_3', purposeAnswer: 'c', name: { en: "PoC", cn: "PoC" }, nodes: NODES_Q_1_3 },
  { id: 'q_1_4', purposeAnswer: 'd', name: { en: "Customer Satisfaction or Billing Errors", cn: "客户满意度 / 账单错误" }, nodes: NODES_Q_1_4 },
  { id: 'q_1_5', purposeAnswer: 'e', name: { en: "Other Purposes", cn: "其他目的" }, nodes: NODES_Q_1_5 },
];

/**
 * The merged walk. `purposeAnswer` is empty because this definition CONTAINS the
 * question that chooses a purpose, rather than being selected by one.
 */
export const SEED_ALL: QuestionnaireDefinition = {
  id: 'q_all',
  purposeAnswer: '',
  name: { en: 'Full questionnaire (frontend entry + all five trees)', cn: '完整问卷（前端入口 + 五棵树）' },
  nodes: NODES_ALL,
};

export const SEED_BY_ID: Record<string, QuestionnaireDefinition> = Object.fromEntries(
  [...SEED_DEFINITIONS, SEED_ALL].map((d) => [d.id, d]),
);
